import React, { ReactNode, useEffect } from 'react';
import './DetailTemplate.css';
import { useOptionalAppHeader } from '../../../layouts/AppHeaderContext';
import type { AppHeaderConfig } from '../../../layouts/AppHeaderContext';

export interface DetailSection {
  /**
   * Section title
   */
  title?: string;

  /**
   * Section content
   */
  content: ReactNode;

  /**
   * Section ID for accessibility
   */
  id?: string;

  /**
   * Optional actions for this section
   */
  actions?: ReactNode;

  /**
   * Whether section is collapsible
   */
  collapsible?: boolean;

  /**
   * Initial collapsed state (if collapsible)
   */
  defaultCollapsed?: boolean;
}

export interface DetailTemplateProps {
  /**
   * Header content (title, breadcrumbs, etc.)
   */
  header?: ReactNode;

  /**
   * Main actions (edit, delete, etc.)
   */
  actions?: ReactNode;

  /**
   * Sections to display
   */
  sections: DetailSection[];

  /**
   * Sidebar content (metadata, related items, etc.)
   */
  sidebar?: ReactNode;

  /**
   * Footer content (save/cancel buttons, etc.)
   */
  footer?: ReactNode;

  /**
   * Loading state
   */
  loading?: boolean;

  /**
   * Error state
   */
  error?: string | null;

  /**
   * Custom container class name
   */
  className?: string;

  /**
   * Layout variant
   */
  layout?: 'single' | 'with-sidebar';

  /**
   * Sidebar position
   */
  sidebarPosition?: 'left' | 'right';

  /**
   * Custom loading component
   */
  loadingComponent?: ReactNode;

  /**
   * AppHeader configuration to use instead of inline header
   */
  appHeader?: AppHeaderConfig;

  /**
   * Use AppHeader when appHeader is provided
   */
  useAppHeader?: boolean;
}

/**
 * DetailTemplate organism
 *
 * Reusable template for detail/view pages with common patterns:
 * - Header with breadcrumbs/title
 * - Action buttons
 * - Organized sections
 * - Optional sidebar for metadata
 * - Footer for form actions
 *
 * @example
 * ```tsx
 * <DetailTemplate
 *   header={
 *     <div>
 *       <Breadcrumbs items={[{ label: 'Users', path: '/users' }, { label: user.name }]} />
 *       <h1>{user.name}</h1>
 *     </div>
 *   }
 *   actions={
 *     <>
 *       <Button onClick={handleEdit}>Edit</Button>
 *       <Button variant="danger" onClick={handleDelete}>Delete</Button>
 *     </>
 *   }
 *   sections={[
 *     {
 *       title: 'Personal Information',
 *       content: <UserPersonalInfo user={user} />
 *     },
 *     {
 *       title: 'Settings',
 *       content: <UserSettings user={user} />,
 *       collapsible: true
 *     }
 *   ]}
 *   sidebar={
 *     <div>
 *       <h3>Metadata</h3>
 *       <p>Created: {user.createdAt}</p>
 *       <p>Updated: {user.updatedAt}</p>
 *     </div>
 *   }
 * />
 * ```
 */
export function DetailTemplate({
  header,
  actions,
  sections,
  sidebar,
  footer,
  loading = false,
  error,
  className = '',
  layout = 'single',
  sidebarPosition = 'right',
  loadingComponent,
  appHeader,
  useAppHeader = false,
}: DetailTemplateProps) {
  const [collapsedSections, setCollapsedSections] = React.useState<Set<number>>(
    new Set(
      sections
        .map((section, index) => (section.defaultCollapsed ? index : -1))
        .filter((index) => index !== -1)
    )
  );
  const appHeaderContext = useOptionalAppHeader();

  useEffect(() => {
    if (!useAppHeader || !appHeader || !appHeaderContext) return;
    appHeaderContext.setHeaderConfig(appHeader);
    return () => appHeaderContext.setHeaderConfig(null);
  }, [appHeader, appHeaderContext, useAppHeader]);

  const showInlineHeader = !(useAppHeader && appHeader && appHeaderContext);

  const toggleSection = (index: number) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const renderLoadingState = () => {
    if (loadingComponent) {
      return loadingComponent;
    }

    return (
      <div className="detail-template__loading">
        <div className="detail-template__spinner" />
        <p>Loading...</p>
      </div>
    );
  };

  const renderErrorState = () => {
    return (
      <div className="detail-template__error">
        <p className="detail-template__error-text">{error}</p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`detail-template ${className}`}>
        {renderLoadingState()}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`detail-template ${className}`}>
        {renderErrorState()}
      </div>
    );
  }

  return (
    <div
      className={`detail-template detail-template--${layout} detail-template--sidebar-${sidebarPosition} ${className}`}
    >
      {/* Header with Actions */}
      {showInlineHeader && (header || actions) && (
        <div className="detail-template__header">
          {header && <div className="detail-template__header-content">{header}</div>}
          {actions && <div className="detail-template__actions">{actions}</div>}
        </div>
      )}

      {/* Main Content Area */}
      <div className="detail-template__body">
        {/* Sidebar (left position) */}
        {layout === 'with-sidebar' && sidebarPosition === 'left' && sidebar && (
          <aside className="detail-template__sidebar">{sidebar}</aside>
        )}

        {/* Main Content */}
        <div className="detail-template__content">
          {sections.map((section, index) => {
            const isCollapsed = collapsedSections.has(index);
            const sectionId = section.id || `section-${index}`;

            return (
              <section
                key={sectionId}
                className={`detail-template__section ${
                  isCollapsed ? 'detail-template__section--collapsed' : ''
                }`}
                id={sectionId}
              >
                {(section.title || section.actions || section.collapsible) && (
                  <div className="detail-template__section-header">
                    {section.title && (
                      <h2 className="detail-template__section-title">
                        {section.collapsible && (
                          <button
                            type="button"
                            className="detail-template__section-toggle"
                            onClick={() => toggleSection(index)}
                            aria-expanded={!isCollapsed}
                            aria-controls={`${sectionId}-content`}
                          >
                            <span
                              className={`detail-template__section-toggle-icon ${
                                isCollapsed ? 'detail-template__section-toggle-icon--collapsed' : ''
                              }`}
                            >
                              ▼
                            </span>
                          </button>
                        )}
                        {section.title}
                      </h2>
                    )}
                    {section.actions && (
                      <div className="detail-template__section-actions">
                        {section.actions}
                      </div>
                    )}
                  </div>
                )}

                {!isCollapsed && (
                  <div
                    className="detail-template__section-content"
                    id={`${sectionId}-content`}
                  >
                    {section.content}
                  </div>
                )}
              </section>
            );
          })}
        </div>

        {/* Sidebar (right position) */}
        {layout === 'with-sidebar' && sidebarPosition === 'right' && sidebar && (
          <aside className="detail-template__sidebar">{sidebar}</aside>
        )}
      </div>

      {/* Footer */}
      {footer && <div className="detail-template__footer">{footer}</div>}
    </div>
  );
}

export default DetailTemplate;
